import { ScreenHeader } from "../../../components/PromotionIQ/ScreenHeader";
import { DataTable } from "../../../components/PromotionIQ/DataTable";
import { StatusBadge } from "../../../components/PromotionIQ/StatusBadge";
import {
  PlannedPromotion,
  plannedPromotions,
  formatCurrency,
  getRetailerName,
  getSkuInfo,
} from "../../../data/PromotionIQ/PromotionData";
import { useNavigate } from "react-router-dom";
import { ChevronRight, Scale, Wrench, Trash2, AlertCircle, Plus, TrendingUp } from "lucide-react";
import React from "react";
import { usePromotionSelection } from "../../../components/PromotionIQ/PromotionSelectionContext";

const PromotionPortfolio = () => {
  const navigate = useNavigate();
  const { setSelectedPromotion } = usePromotionSelection();

  // Group by auto-tag
  const scalePromos = plannedPromotions.filter(p => p.auto_tag === "Scale");
  const fixPromos = plannedPromotions.filter(p => p.auto_tag === "Fix");
  const killPromos = plannedPromotions.filter(p => p.auto_tag === "Kill");
  const reviewPromos = plannedPromotions.filter(p => p.auto_tag === "Review");

  const totalInvestment = plannedPromotions.reduce((sum, p) => sum + p.planned_trade_spend, 0);
  const totalExpectedMargin = plannedPromotions.reduce((sum, p) => sum + p.expected_margin, 0);

  const getAutoTagIcon = (tag: string) => {
    switch (tag) {
      case "Scale": return <Scale className="h-4 w-4 text-success" />;
      case "Fix": return <Wrench className="h-4 w-4 text-warning" />;
      case "Kill": return <Trash2 className="h-4 w-4 text-destructive" />;
      case "Review": return <AlertCircle className="h-4 w-4 text-primary" />;
      default: return null;
    }
  };

  const getAutoTagStyle = (tag: string) => {
    switch (tag) {
      case "Scale": return "bg-success/20 text-success";
      case "Fix": return "bg-warning/20 text-warning";
      case "Kill": return "bg-destructive/20 text-destructive";
      case "Review": return "bg-primary/20 text-primary";
      default: return "bg-muted text-muted-foreground";
    }
  };

  const handleCategoryClick = (tag: string) => {
    // Filter and scroll to relevant promotions
    const element = document.getElementById("promotion-table");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="space-y-6">
      <ScreenHeader
        title="Promotion Portfolio"
        question="What are we currently planning to fund?"
      />

      {/* Summary Cards - Interactive */}
      <div className="grid grid-cols-4 gap-4">
        <div 
          onClick={() => handleCategoryClick("Scale")}
          className="border border-success/30 bg-success/5 p-4 cursor-pointer hover:border-success hover:bg-success/10 transition-all group"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Scale className="h-5 w-5 text-success" />
              <span className="font-medium text-success">SCALE</span>
            </div>
            <ChevronRight className="h-4 w-4 text-success opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
          <p className="text-3xl font-bold mt-2">{scalePromos.length}</p>
          <p className="text-xs text-muted-foreground">High performers to expand</p>
          <div className="mt-2 pt-2 border-t border-success/20">
            <p className="text-xs text-success">
              {formatCurrency(scalePromos.reduce((sum, p) => sum + p.expected_margin, 0))} expected
            </p>
          </div>
        </div>
        <div 
          onClick={() => handleCategoryClick("Fix")}
          className="border border-warning/30 bg-warning/5 p-4 cursor-pointer hover:border-warning hover:bg-warning/10 transition-all group"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Wrench className="h-5 w-5 text-warning" />
              <span className="font-medium text-warning">FIX</span>
            </div>
            <ChevronRight className="h-4 w-4 text-warning opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
          <p className="text-3xl font-bold mt-2">{fixPromos.length}</p>
          <p className="text-xs text-muted-foreground">Needs optimization</p>
          <div className="mt-2 pt-2 border-t border-warning/20">
            <p className="text-xs text-warning">
              {formatCurrency(fixPromos.reduce((sum, p) => sum + p.expected_margin, 0))} at risk
            </p>
          </div>
        </div>
        <div 
          onClick={() => handleCategoryClick("Kill")}
          className="border border-destructive/30 bg-destructive/5 p-4 cursor-pointer hover:border-destructive hover:bg-destructive/10 transition-all group"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-destructive" />
              <span className="font-medium text-destructive">KILL</span>
            </div>
            <ChevronRight className="h-4 w-4 text-destructive opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
          <p className="text-3xl font-bold mt-2">{killPromos.length}</p>
          <p className="text-xs text-muted-foreground">Consider eliminating</p>
          <div className="mt-2 pt-2 border-t border-destructive/20">
            <p className="text-xs text-destructive">
              {formatCurrency(killPromos.reduce((sum, p) => sum + p.planned_trade_spend, 0))} to save
            </p>
          </div>
        </div>
        <div 
          onClick={() => handleCategoryClick("Review")}
          className="border border-primary/30 bg-primary/5 p-4 cursor-pointer hover:border-primary hover:bg-primary/10 transition-all group"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-primary" />
              <span className="font-medium text-primary">REVIEW</span>
            </div>
            <ChevronRight className="h-4 w-4 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
          <p className="text-3xl font-bold mt-2">{reviewPromos.length}</p>
          <p className="text-xs text-muted-foreground">Requires analysis</p>
          <div className="mt-2 pt-2 border-t border-primary/20">
            <p className="text-xs text-primary">
              {formatCurrency(reviewPromos.reduce((sum, p) => sum + p.planned_trade_spend, 0))} pending
            </p>
          </div>
        </div>
      </div>

      {/* Action Bar */}
      <div className="flex items-center justify-between border border-border bg-card p-4">
        <div className="flex items-center gap-6">
          <div>
            <p className="text-sm text-muted-foreground">Total Investment</p>
            <p className="text-2xl font-bold text-primary">{formatCurrency(totalInvestment)}</p>
          </div>
          <div className="h-8 w-px bg-border" />
          <div>
            <p className="text-sm text-muted-foreground">Expected Margin</p>
            <p className="text-2xl font-bold text-success">{formatCurrency(totalExpectedMargin)}</p>
          </div>
          <div className="h-8 w-px bg-border" />
          <div>
            <p className="text-sm text-muted-foreground">Portfolio ROI</p>
            <p className="text-2xl font-bold">{((totalExpectedMargin / totalInvestment) + 1).toFixed(2)}x</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              setSelectedPromotion(null);
              navigate("/promotioniq/scenarios");
            }}
            className="btn-athena"
          >
            <TrendingUp className="h-4 w-4 mr-2" />
            Compare Scenarios
          </button>
          <button
            onClick={() => {
              setSelectedPromotion(null);
              navigate("/promotioniq/promotion-builder");
            }}
            className="btn-athena-filled"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Investment
          </button>
        </div>
      </div>

      {/* Portfolio Table */}
      <div id="promotion-table" className="border border-border bg-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="section-header">Next Cycle Investments</h3>
          <p className="text-sm text-muted-foreground">Click any row to edit promotion</p>
        </div>
        <DataTable<PlannedPromotion>
          data={plannedPromotions}
          onRowClick={(promo) => {
            setSelectedPromotion(promo.promotion_id);
            navigate("/promotioniq/promotion-builder");
          }}
          columns={[
            {
              key: "auto_tag",
              header: "Tag",
              render: (item) => (
                <div className="flex items-center gap-2">
                  {getAutoTagIcon(item.auto_tag)}
                  <span className={`px-2 py-0.5 text-xs ${getAutoTagStyle(item.auto_tag)}`}>
                    {item.auto_tag}
                  </span>
                </div>
              ),
            },
            { 
              key: "promotion_id", 
              header: "Promo ID",
              render: (item) => (
                <span className="font-medium text-primary">{item.promotion_id}</span>
              ),
            },
            {
              key: "retailer_id",
              header: "Retailer",
              render: (item) => (
                <span 
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/promotioniq/retailer-economics/${item.retailer_id}`);
                  }}
                  className="text-foreground hover:text-primary hover:underline cursor-pointer"
                >
                  {getRetailerName(item.retailer_id)}
                </span>
              ),
            },
            {
              key: "sku_id",
              header: "SKU / Brand",
              render: (item) => getSkuInfo(item.sku_id),
            },
            {
              key: "promo_window",
              header: "Promotion Window",
              render: (item) => item.promo_window,
            },
            {
              key: "planned_trade_spend",
              header: "Investment",
              render: (item) => formatCurrency(item.planned_trade_spend),
            },
            {
              key: "expected_incremental_revenue",
              header: "Expected Incr.",
              render: (item) => (
                <span className="text-primary">{formatCurrency(item.expected_incremental_revenue)}</span>
              ),
            },
            {
              key: "expected_margin",
              header: "Expected Margin",
              render: (item) => (
                <span className={item.expected_margin >= 0 ? "text-success" : "text-destructive"}>
                  {formatCurrency(item.expected_margin)}
                </span>
              ),
            },
            {
              key: "risk_level",
              header: "Risk",
              render: (item) => <StatusBadge status={item.risk_level} />,
            },
            {
              key: "confidence",
              header: "Confidence",
              render: (item) => (
                <div className="flex items-center gap-2">
                  <div className="h-2 w-16 bg-muted">
                    <div
                      className={`h-2 ${item.confidence >= 0.75 ? "bg-success" : item.confidence >= 0.6 ? "bg-warning" : "bg-destructive"}`}
                      style={{ width: `${item.confidence * 100}%` }}
                    />
                  </div>
                  <span className="text-xs">{Math.round(item.confidence * 100)}%</span>
                </div>
              ),
            },
            {
              key: "status",
              header: "Status",
              render: (item) => <StatusBadge status={item.status} />,
            },
            {
              key: "action",
              header: "",
              render: () => (
                <button className="btn-athena py-1 px-3">
                  <span>Edit</span>
                  <ChevronRight className="h-4 w-4 ml-1" />
                </button>
              ),
            },
          ]}
        />
      </div>

      {/* Related Actions */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div 
          onClick={() => navigate("/promotioniq/shopper-influence")}
          className="border border-border bg-card p-4 cursor-pointer hover:border-primary hover:bg-primary/5 transition-all group"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium group-hover:text-primary transition-colors">Shopper Influence Stack</p>
              <p className="text-sm text-muted-foreground">See which mechanisms drive demand</p>
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
          </div>
        </div>
        <div 
          onClick={() => navigate("/promotioniq/impact-panel")}
          className="border border-border bg-card p-4 cursor-pointer hover:border-primary hover:bg-primary/5 transition-all group"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium group-hover:text-primary transition-colors">Impact Panel</p>
              <p className="text-sm text-muted-foreground">Analyze geo-fencing & digital tools</p>
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
          </div>
        </div>
        <div 
          onClick={() => navigate("/promotioniq/monitoring")}
          className="border border-border bg-card p-4 cursor-pointer hover:border-primary hover:bg-primary/5 transition-all group"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium group-hover:text-primary transition-colors">In-Cycle Monitoring</p>
              <p className="text-sm text-muted-foreground">Track actual vs expected performance</p>
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
          </div>
        </div>
      </div>

      {/* Key Insight */}
      <div className="border border-primary/50 bg-primary/5 p-6">
        <p className="text-sm text-muted-foreground uppercase tracking-wide mb-2">Purpose</p>
        <p className="text-lg">
          This shifts focus from <span className="line-through text-muted-foreground">calendars</span> to 
          <span className="text-primary font-bold"> investment quality</span>. 
          Each promotion is tagged based on performance potential, not just timing.
        </p>
      </div>
    </div>
  );
};

export default PromotionPortfolio;
